import { useEffect, useState } from 'react';
import { Box, Group, Text, Slider } from '@mantine/core';
import {
  IconMicrophone,
  IconPlayerPause,
  IconPlayerPlay,
  IconSquare,
  IconTrash,
  IconVolume,
  IconVolumeOff,
} from '@tabler/icons-react';
import { formatMmSs } from '../../voice';
import {
  useWebmOpusRecorderPlayback,
  useWebmOpusRecording,
  type WebmOpusRecordingError,
} from '../../react';
import {
  MediaToolbarIcon,
  MediaToolbarRow,
} from '../media-toolbar/media-toolbar';
import { RecordingWaveformScroller } from './recording-waveform-scroller';

export type VoiceRecorderLabels = {
  startRecording: string;
  startHint: string;
  stopRecording: string;
  play: string;
  pause: string;
  mute: string;
  unmute: string;
  delete: string;
  browserUnsupported: string;
  errors: Record<WebmOpusRecordingError, string>;
};

export type VoiceRecorderProps = {
  /** Вызывается после успешной остановки записи */
  onRecordingComplete?: (payload: { blob: Blob; mimeType: string }) => void;
  /** Сброс готовой записи (удаление) */
  onRecordingReset?: () => void;
  className?: string;
  disabled?: boolean;
  setIsRecording?: (isRecording: boolean) => void;
  labels: VoiceRecorderLabels;
};

export function VoiceRecorder({
  onRecordingComplete,
  onRecordingReset,
  disabled = false,
  setIsRecording,
  labels,
}: VoiceRecorderProps) {
  const [isGrubbing, setIsGrubbing] = useState(false);
  const {
    status,
    error,
    elapsedMs,
    recordingDurationMs,
    blob,
    mediaStream,
    canRecord,
    startRecording,
    stopRecording,
    clearRecording,
  } = useWebmOpusRecording({ onRecordingComplete });

  const {
    audioRef,
    isPlaying,
    togglePlayback,
    currentTime,
    displayDuration,
    progressPercent,
    volume,
    isMuted,
    onSeekPercent,
    onVolumePercent,
    onToggleMute,
  } = useWebmOpusRecorderPlayback({ blob, recordingDurationMs });

  const isRecording = status === 'recording';
  const hasBlob = Boolean(blob);
  const blocked = disabled || !canRecord;

  useEffect(() => {
    setIsRecording?.(isRecording);
  }, [isRecording]);

  const playbackCurrentLabel = formatMmSs(Math.floor(currentTime * 1000));
  const playbackTotalLabel =
    displayDuration > 0
      ? formatMmSs(Math.floor(displayDuration * 1000))
      : '—:—';

  const playLabel = isPlaying ? labels.pause : labels.play;
  const muteLabel = isMuted ? labels.unmute : labels.mute;

  return (
    <Box
      p={16}
      style={{ borderRadius: 16 }}
      bd='1px solid var(--mantine-color-primary-0)'
    >
      <audio
        ref={audioRef}
        style={{ display: 'none' }}
        preload='auto'
        controls={false}
      >
        <track kind='captions' />
      </audio>

      {!isRecording && !hasBlob && (
        <MediaToolbarRow>
          <MediaToolbarIcon
            label={labels.startRecording}
            disabled={blocked}
            onClick={() => void startRecording()}
          >
            <IconMicrophone size={20} />
          </MediaToolbarIcon>
          <Text flex={1} size='sm' c='tertiary.7' ta='left' truncate>
            {labels.startHint}
          </Text>
          <Text size='sm' c='tertiary.7' w={40}>
            {formatMmSs(0)}
          </Text>
        </MediaToolbarRow>
      )}

      {isRecording && (
        <MediaToolbarRow>
          <MediaToolbarIcon
            label={labels.stopRecording}
            onClick={stopRecording}
          >
            <IconSquare size={20} />
          </MediaToolbarIcon>
          <RecordingWaveformScroller stream={mediaStream} active />
          <Group style={{ flexShrink: 0 }} align='center' gap={8} wrap='nowrap'>
            <Box
              w={8}
              h={8}
              bg='red.7'
              style={{ borderRadius: '50%', flexShrink: 0 }}
              aria-hidden
            />
            <Text size='sm' c='tertiary.7' w={40}>
              {formatMmSs(elapsedMs)}
            </Text>
          </Group>
        </MediaToolbarRow>
      )}

      {!isRecording && hasBlob && (
        <MediaToolbarRow>
          <MediaToolbarIcon label={playLabel} onClick={togglePlayback}>
            {isPlaying ? (
              <IconPlayerPause size={20} />
            ) : (
              <IconPlayerPlay size={20} />
            )}
          </MediaToolbarIcon>
          <Slider
            flex={1}
            value={progressPercent}
            onChange={onSeekPercent}
            onPointerDown={() => setIsGrubbing(true)}
            onPointerUp={() => setIsGrubbing(false)}
            min={0}
            max={100}
            step={0.1}
            label={playbackCurrentLabel}
            color='blue'
            size='sm'
            styles={{
              thumb: {
                cursor: isGrubbing ? 'grabbing' : 'grab',
              },
              label: {
                backgroundColor: 'var(--mantine-color-primary-0)',
                color: 'black',
              },
            }}
          />
          <Text
            size='sm'
            c='tertiary.7'
            ta='center'
            style={{
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {playbackCurrentLabel} / {playbackTotalLabel}
          </Text>
          <MediaToolbarIcon label={muteLabel} onClick={onToggleMute}>
            {isMuted || volume === 0 ? (
              <IconVolumeOff size={20} />
            ) : (
              <IconVolume size={20} />
            )}
          </MediaToolbarIcon>
          <Slider
            w={40}
            value={isMuted ? 0 : volume * 100}
            onChange={onVolumePercent}
            onPointerDown={() => setIsGrubbing(true)}
            onPointerUp={() => setIsGrubbing(false)}
            min={0}
            max={100}
            step={1}
            label={null}
            color='blue'
            size='sm'
            styles={{
              thumb: {
                cursor: isGrubbing ? 'grabbing' : 'grab',
              },
            }}
          />
          <MediaToolbarIcon
            label={labels.delete}
            disabled={disabled}
            onClick={() => {
              clearRecording();
              onRecordingReset?.();
            }}
          >
            <IconTrash size={20} />
          </MediaToolbarIcon>
        </MediaToolbarRow>
      )}

      {error && (
        <Text size='xs' c='red' mt='xs'>
          {labels.errors[error]}
        </Text>
      )}

      {(!canRecord || status === 'unsupported') && (
        <Text size='xs' c='dimmed' mt='xs'>
          {labels.browserUnsupported}
        </Text>
      )}
    </Box>
  );
}
